import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { ArrowLeft, Printer, Award, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { ShareModal } from "../components/ShareModal";

export default function Certificate() {
  const { studentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const certRef = useRef(null);

  useEffect(() => {
    api.get("/educator/students").then(res => {
      const found = (res.data.students || []).find(s => s.id === studentId);
      setStudent(found);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [studentId]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-zinc-400">Student not found</p>
      </div>
    );
  }

  const sections = student.progress?.sections_completed || 0;
  const pct = Math.round((sections / 21) * 100);
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const xp = student.progress?.total_xp || 0;
  const badges = student.progress?.badges_count || 0;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="certificate-page">
      {/* Screen-only header */}
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white" data-testid="cert-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">Certificate</h1>
          <Button onClick={handlePrint} className="btn-primary" data-testid="print-cert-btn">
            <Printer size={16} className="mr-2" /> Print
          </Button>
          <button onClick={() => setShareOpen(true)} className="text-[#F7931A] hover:text-[#F7931A]/80 ml-3" data-testid="share-cert-btn">
            <Share2 size={20} />
          </button>
        </div>
      </header>

      {/* Certificate — print-optimized */}
      <div className="max-w-3xl mx-auto px-4 py-8 print:px-0 print:py-0">
        <div ref={certRef}
          className="bg-white text-black p-12 rounded-2xl print:rounded-none print:shadow-none shadow-2xl"
          style={{ minHeight: "700px" }}
          data-testid="certificate-document">

          {/* Border frame */}
          <div className="border-4 border-[#F7931A] p-8 relative" style={{ minHeight: "600px" }}>
            {/* Corner accents */}
            <div className="absolute top-2 left-2 w-8 h-8 border-t-2 border-l-2 border-[#F7931A]" />
            <div className="absolute top-2 right-2 w-8 h-8 border-t-2 border-r-2 border-[#F7931A]" />
            <div className="absolute bottom-2 left-2 w-8 h-8 border-b-2 border-l-2 border-[#F7931A]" />
            <div className="absolute bottom-2 right-2 w-8 h-8 border-b-2 border-r-2 border-[#F7931A]" />

            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Award className="text-[#F7931A]" size={36} />
                <h1 style={{ fontFamily: "Georgia, serif", fontSize: "32px", color: "#F7931A", fontWeight: "bold" }}>
                  Bitcoin Brain
                </h1>
              </div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#888", letterSpacing: "2px", textTransform: "uppercase" }}>
                Certificate of Completion
              </p>
            </div>

            {/* Divider */}
            <div className="w-32 h-0.5 bg-[#F7931A] mx-auto mb-8" />

            {/* Body */}
            <div className="text-center mb-8">
              <p style={{ fontFamily: "Georgia, serif", fontSize: "16px", color: "#666", marginBottom: "12px" }}>
                This certifies that
              </p>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: "36px", color: "#1a1a1a", fontWeight: "bold", borderBottom: "2px solid #F7931A", display: "inline-block", paddingBottom: "4px" }}>
                {student.student_name}
              </h2>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "16px", color: "#666", marginTop: "16px", lineHeight: "1.8" }}>
                has successfully completed <strong style={{ color: "#1a1a1a" }}>{sections} of 21 sections</strong> ({pct}%)
                <br />of the Bitcoin Brain educational course
              </p>
            </div>

            {/* Stats */}
            <div className="flex justify-center gap-8 mb-8">
              <div className="text-center">
                <p style={{ fontSize: "24px", fontWeight: "bold", color: "#F7931A", fontFamily: "monospace" }}>{xp.toLocaleString()}</p>
                <p style={{ fontSize: "12px", color: "#888" }}>BTC XP Earned</p>
              </div>
              <div className="text-center">
                <p style={{ fontSize: "24px", fontWeight: "bold", color: "#F7931A", fontFamily: "monospace" }}>{badges}</p>
                <p style={{ fontSize: "12px", color: "#888" }}>Badges Earned</p>
              </div>
              <div className="text-center">
                <p style={{ fontSize: "24px", fontWeight: "bold", color: "#F7931A", fontFamily: "monospace" }}>{pct}%</p>
                <p style={{ fontSize: "12px", color: "#888" }}>Course Complete</p>
              </div>
            </div>

            {/* Divider */}
            <div className="w-48 h-0.5 bg-[#F7931A]/30 mx-auto mb-8" />

            {/* Footer */}
            <div className="flex justify-between items-end">
              <div className="text-left">
                <p style={{ fontSize: "12px", color: "#888" }}>Date Issued</p>
                <p style={{ fontSize: "14px", color: "#333", fontWeight: "500" }}>{today}</p>
              </div>
              <div className="text-center">
                <div style={{ borderBottom: "1px solid #ccc", width: "200px", marginBottom: "4px" }} />
                <p style={{ fontSize: "12px", color: "#888" }}>Bryce Fields — Founder, Bitcoin Brain</p>
              </div>
              <div className="text-right">
                <p style={{ fontSize: "12px", color: "#888" }}>Educator</p>
                <p style={{ fontSize: "14px", color: "#333", fontWeight: "500" }}>{user?.first_name || "Educator"}</p>
              </div>
            </div>

            {/* Bottom tagline */}
            <p className="text-center mt-6" style={{ fontSize: "10px", color: "#bbb", fontStyle: "italic" }}>
              "Learn Bitcoin. Own It." — bitcoinbrain.space
            </p>
          </div>
        </div>
      </div>

      <ShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)}
        text={`${student.student_name} completed ${sections} of 21 sections on Bitcoin Brain! Learning sound money, one section at a time.`}
        url="https://bitcoinbrain.space"
        title={`Bitcoin Brain Certificate: ${student.student_name}`} />
    </div>
  );
}
